import { normalizeDateToUTC } from "./dateUtils";
import { limitDecimals } from "./floatPrecision";

// datagrid column definitions
type DatagridColumnDefinition = {
    field: string;
    type: string;
};

// type for a data row
type DataRow = any;

// constant for the number of decimal places
const DEFAULT_DECIMAL_PLACES: number = 2;


/**
 * Prepares raw data rows by applying necessary transformations based on column definitions.
 * This includes:
 * 1. Formatting numeric ('number') fields to a specified decimal precision.
 * 2. Normalizing date ('date') fields to UTC start-of-day Date objects.
 *
 * @param {DataRow[]} dataRows - Array of data objects to be processed.
 * @param {DatagridColumnDefinition[]} columnDefinitions - Array of objects defining the data schema.
 * @param {number} [decimalPlaces=2] - The number of decimal places for numeric fields. Defaults to 2.
 * @returns {DataRow[]} A new array of data objects with the fields transformed.
 */
export function prepareGridData(
    dataRows: DataRow[],
    columnDefinitions: DatagridColumnDefinition[],
    decimalPlaces: number = DEFAULT_DECIMAL_PLACES
): DataRow[] {
    const transformFields = columnDefinitions.reduce(
        (acc, col) => {
            if (col.type === "number") {
                acc.number.add(col.field);
            } else if (col.type === "date") {
                acc.date.add(col.field);
            }
            return acc;
        },
        { number: new Set<string>(), date: new Set<string>() }
    );

    const numberFieldsToFormat = transformFields.number;
    const dateFieldsToFormat = transformFields.date;

    const transformedDataRows: DataRow[] = dataRows.map((row: DataRow) => {
        const newRow: DataRow = { ...row }; 

        for (const fieldName in newRow) {
            if (numberFieldsToFormat.has(fieldName)) {
                // Apply numeric precision limiting
                newRow[fieldName] = limitDecimals(newRow[fieldName], decimalPlaces);
            } 
            
            else if (dateFieldsToFormat.has(fieldName)) {
                // Apply date normalization 
                const value = newRow[fieldName];
                if (value !== null && value !== undefined) {
                    newRow[fieldName] = normalizeDateToUTC(value);
                }
            }
        }

        return newRow;
    });

    return transformedDataRows;
}
